// =======================================================================================================
import * as PrefixUtils from "../Utils/Prefix"
import _ from 'lodash';
import { IsNullOrWhiteSpace, IsNullOrEmpty } from "./Micro";
// =======================================================================================================
export class DomUtils {
    // -------------------------------------------------------------------------------------------------------
    static findInArray(array: Array<any>, callback: Function): any {
        for (let i = 0, length = array.length; i < length; i++) {
            if (callback.apply(callback, [array[i], i, array])) return array[i];
        }
    }
    // -------------------------------------------------------------------------------------------------------
    // duplicate function! todo: replace with Utils.cs
    static isFunction(func: any): boolean {
        return typeof func === 'function' || Object.prototype.toString.call(func) === '[object Function]';
    }
    // duplicate function! todo: replace with Utils.cs
    static isNum(num: any): boolean {
        return typeof num === 'number' && !isNaN(num);
    }
    // -------------------------------------------------------------------------------------------------------
    static dontSetMe(props: Object, propName: string, componentName: string) {
        if (props[propName]) {
            return new Error(`Invalid prop ${propName} passed to ${componentName} - do not set this, set it on the child.`);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    static isTouchEvent(object: any): object is TouchEvent {
        return 'targetTouches' in object;
    }
    // =======================================================================================================
    // Events
    // =======================================================================================================
    static addEvent(el: Node, event: string, handler: EventListenerOrEventListenerObject): void {
        if (!el) { return; }
        if (el.addEventListener) {
            el.addEventListener(event, handler, true);
        } else {
            // $FlowIgnore: Doesn't think elements are indexable
            el['on' + event] = handler;
        }
    }
    // -------------------------------------------------------------------------------------------------------
    static removeEvent(el: Node, event: string, handler: EventListenerOrEventListenerObject): void {
        if (!el) { return; }
        if (el.removeEventListener) {
            el.removeEventListener(event, handler);
        } else {
            // $FlowIgnore: Doesn't think elements are indexable
            el['on' + event] = null;
        }
    }
    // -------------------------------------------------------------------------------------------------------
    // Get from offsetParent
    static offsetXYFromParentOf(e: MouseEvent | TouchEvent, node: HTMLElement) {
        const evt = DomUtils.isTouchEvent(e) ? e.targetTouches[0] : e;

        const offsetParent = node.offsetParent || document.body;
        const offsetParentRect = node.offsetParent === document.body ? { left: 0, top: 0 } : offsetParent.getBoundingClientRect();

        const x = evt.clientX + offsetParent.scrollLeft - offsetParentRect.left;
        const y = evt.clientY + offsetParent.scrollTop - offsetParentRect.top;

        return { x, y };
    }
    // -------------------------------------------------------------------------------------------------------
    static createCSSTransform({ x, y }: { x: number, y: number }): Object {
        // Replace unitless items with px
        return { [PrefixUtils.browserPrefixToKey('transform', PrefixUtils.getPrefix())]: 'translate(' + x + 'px,' + y + 'px)' };
    }
    // -------------------------------------------------------------------------------------------------------
    static createSVGTransform({ x, y }: { x: number, y: number }): string {
        return 'translate(' + x + ',' + y + ')';
    }
    // -------------------------------------------------------------------------------------------------------
    // User-select Hacks:
    //
    // Useful for preventing blue highlights all over everything when dragging.
    static userSelectPrefix = PrefixUtils.getPrefix('user-select');
    static userSelect = PrefixUtils.browserPrefixToStyle('user-select', PrefixUtils.getPrefix('user-select'));
    static userSelectStyle = `;${PrefixUtils.browserPrefixToStyle('user-select', PrefixUtils.getPrefix('user-select'))}: none;`;
    // -------------------------------------------------------------------------------------------------------
    static addUserSelectStyles() {
        const style = document.body.getAttribute('style') || '';
        document.body.setAttribute('style', style + this.userSelectStyle);
    }
    // -------------------------------------------------------------------------------------------------------
    static removeUserSelectStyles() {
        const style = document.body.getAttribute('style') || '';
        document.body.setAttribute('style', style.replace(this.userSelectStyle, ''));
    }
    // -------------------------------------------------------------------------------------------------------
    static styleHacks(childStyle: Object = {}): Object {
        // Workaround IE pointer events; see #51
        // https://github.com/mzabriskie/react-draggable/issues/51#issuecomment-103488278
        return { touchAction: 'none' } || childStyle;
    }
    // =======================================================================================================
    static matchesSelectorFunc: string = '';
    static matchesSelector(el: EventTarget, selector: string): boolean {
        if (!this.matchesSelectorFunc) {
            this.matchesSelectorFunc = this.findInArray([
                'matches',
                'webkitMatchesSelector',
                'mozMatchesSelector',
                'msMatchesSelector',
                'oMatchesSelector'
            ], function (method) {
                // $FlowIgnore: Doesn't think elements are indexable
                return DomUtils.isFunction(el[method]);
            });
        }

        // $FlowIgnore: Doesn't think elements are indexable
        return el[this.matchesSelectorFunc].call(el, selector);
    }
    // =======================================================================================================

    // ------------------------------------------------------------------------------------------------------
    private static calculateAspectRatioFit(srcWidth: number, srcHeight: number, maxWidth: number, maxHeight: number): { width: number, height: number } {
        var ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
        return { width: srcWidth * ratio, height: srcHeight * ratio };
    }

    // ------------------------------------------------------------------------------------------------------
    static async getBase64(value: string): Promise<string> {
        return await new Promise<string>((resolve, reject) => {
            DomUtils.LoadImage(value).then((img) => {
                let canvas = document.createElement('CANVAS') as HTMLCanvasElement;
                let ctx = canvas.getContext('2d');
                var dataURL;
                canvas.height = img.height;
                canvas.width = img.width;
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            }).catch((reason: Error) => {
                reject(reason);
            });
        });
    }
    // ------------------------------------------------------------------------------------------------------
    static async LoadImage(value: string): Promise<HTMLImageElement> {
        return await new Promise<HTMLImageElement>((resolve, reject) => {
            if (IsNullOrEmpty(value) || IsNullOrWhiteSpace(value)) {
                reject(new Error(""));
            }
            else {
                // create image element
                let img = document.createElement("img");
                img.crossOrigin = "Anonymous";

                // handle error
                img.onerror = (er: ErrorEvent) => {
                    reject(new Error(er.message));
                };

                // handle success
                img.onload = (ev: Event) => {
                    resolve(img);
                }
                // assign source
                img.src = value;
            }
        });
    }
    // ------------------------------------------------------------------------------------------------------
    static IMG_TYPE_PNG = 'png';
    static IMG_TYPE: string[] = [DomUtils.IMG_TYPE_PNG, 'gif', 'bmp', 'jpeg', 'webp'];
    // ------------------------------------------------------------------------------------------------------
    static resize(img: HTMLImageElement, width: number, height: number, type: string, quality: number = 0.8) {
        if (this.IMG_TYPE.indexOf(type) < 0) {
            type = DomUtils.IMG_TYPE_PNG;
        }

        // calculate the width or height , maintaining aspect ratio
        var detImg = img.width / img.height;
        if ((width / height) > detImg) {
            height = width / detImg;
        } else {
            width = height * detImg;
        }

        // Create canvas for resize
        var canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        var ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        (ctx as any).mozImageSmoothingEnabled = true;
        (ctx as any).webkitImageSmoothingEnabled = true;
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(img, 0, 0, width, height);

        // set background color to #fff while output type is NOT PNG
        if (type !== DomUtils.IMG_TYPE_PNG) {
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            //ctx.globalCompositeOperation = '';
        }
        return canvas.toDataURL('image/' + type, quality);
    }
    // ------------------------------------------------------------------------------------------------------
    static async getResizedImage(
        value: string,
        maxWidth: number,
        maxHeight: number,
        outImageType: ImageFormat = "jpeg",
        outImageQuality: number = 0.8)
        : Promise<ImageInfo> {

        return await new Promise<ImageInfo>((resolve, reject) => {
            DomUtils.LoadImage(value).then((img) => {
                let newValue = value;
                let dimensions: { width: number, height: number } = {
                    width: img.width,
                    height: img.height
                };

                // check width/height and resize image if needed
                // if an url was provided, convert to base64
                // or the received type is not the same with the out image
                if (img.width > maxWidth ||
                    img.height > maxHeight ||
                    _.includes(value, "http") ||
                    !_.includes(value, "image/" + outImageType)) {
                    // calculate width and height, taking into account max width/height, maintaining aspect ratio
                    newValue = this.resize(img, maxWidth, maxHeight, outImageType, outImageQuality);
                }

                // get new value from resized image
                resolve({ src: newValue, width: dimensions.width, height: dimensions.height, aspectRatio: dimensions.width / dimensions.height });
            }).catch((reason: Error) => {
                reject(reason);
            });
        });
    }
    // ------------------------------------------------------------------------------------------------------
};
// ------------------------------------------------------------------------------------------------------
export interface ImageInfo {
    /**
    * Image source
    */
    src: string;

    /**
     * Image's width
     */
    width: number;

    /**
     * Image's height
     */
    height: number;

    /**
     * Image's aspect ratio
     */
    aspectRatio: number;
}
export type ImageFormat = "png" | "gif" | "bmp" | "jpeg" | "webp";
// ------------------------------------------------------------------------------------------------------